import { LogOut, User as UserIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/lib/auth-store';

interface Props {
  name: string | null | undefined;
  email: string | null | undefined;
}

function initials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  const first = parts[0] ?? '';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1] ?? '';
  return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase();
}

export function UserMenu({ name, email }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const signOut = useAuthStore((s) => s.signOut);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  async function handleSignOut() {
    setOpen(false);
    await signOut();
    navigate('/sign-in', { replace: true });
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label="Menu do usuário"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex h-9 w-9 items-center justify-center rounded-full text-xs font-semibold transition',
          'bg-[var(--accent)] text-[var(--accent-fg)] hover:opacity-90',
        )}
      >
        {initials(name)}
      </button>

      {open && (
        <div
          role="menu"
          className={cn(
            'absolute right-0 top-full z-50 mt-2 w-56 overflow-hidden rounded-md border shadow-lg',
            'border-[var(--popover-border,var(--card-border))] bg-[var(--popover-bg,var(--card-bg))] backdrop-blur-[var(--blur)]',
          )}
        >
          <div className="border-b border-[var(--border)] px-4 py-3">
            <p className="truncate text-sm font-medium text-[var(--fg)]">
              {name ?? 'Usuário'}
            </p>
            {email && (
              <p className="truncate text-xs text-[var(--fg-muted)]">{email}</p>
            )}
          </div>
          <div className="py-1">
            <Link
              to="/escritorio/configuracoes"
              role="menuitem"
              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-[var(--fg)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)]"
              onClick={() => setOpen(false)}
            >
              <UserIcon className="h-4 w-4" />
              Perfil
            </Link>
            <button
              type="button"
              role="menuitem"
              onClick={handleSignOut}
              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-[var(--fg)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)]"
            >
              <LogOut className="h-4 w-4" />
              Sair
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
